import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { COMPANY } from './siteConfig';
import { STATIC_TITLE_EL } from './staticHead';
import { notifySeoCommit } from './seoCommit';

// The raw HTML shell (public/index.html, the dev-server middleware, or the build-time
// pre-render) carries a plain <title> plus meta/canonical/JSON-LD tags marked
// data-static-seo so non-JS crawlers see a complete page. Once React mounts, the page
// renders its own head tags, so we drop the static ones to avoid duplicates.
//
// Hydration caveat (Phase 59): React 19 renders Helmet's tags as hoistables and, when it
// hydrates, ADOPTS a matching element already in <head> (same name/property + content, or same
// rel + href) as that tag's live instance — the extra data-static-seo attribute does not prevent
// the match. Those elements now belong to React: removing them here would leave React holding a
// detached node and crash with "Cannot read properties of null (reading 'removeChild')" the
// moment the page unmounts (client-side navigation). React stamps every node it owns with a
// `__reactFiber$…` / `__reactMarker$…` property, so only unowned static tags are removed.
const isReactOwned = (el) => Object.keys(el).some((k) => k.startsWith('__reactFiber$') || k.startsWith('__reactMarker$'));
const dropStaticTitle = () => {
  if (STATIC_TITLE_EL?.isConnected && !isReactOwned(STATIC_TITLE_EL) && document.head.querySelectorAll('title').length > 1) {
    STATIC_TITLE_EL.remove();
  }
};
const useDropStaticSeoTags = () => {
  useEffect(() => {
    document.head.querySelectorAll('[data-static-seo]').forEach((el) => { if (!isReactOwned(el)) el.remove(); });
    dropStaticTitle();
    // Helmet may commit its <title> a frame later than this effect; check once more.
    const raf = requestAnimationFrame(dropStaticTitle);
    return () => cancelAnimationFrame(raf);
  }, []);
};

/**
 * Per-route head tags. `noindex` is for pages that must never be indexed (the 404 page):
 * it emits robots noindex and omits the canonical/OG tags so nothing points crawlers at it.
 *
 * Page-view timing: under React 19, react-helmet-async renders <title>/<meta> as native React
 * hoistable elements (its React19Dispatcher), so Helmet's onChangeClientState callback never
 * runs. React inserts the hoisted <title> during the commit phase, i.e. before passive effects,
 * so an effect keyed on `title` is the reliable "the real title is now in the DOM" signal. It
 * runs after the static-tag cleanup effect above (effects fire in declaration order) and tells
 * the page-view tracker via lib/seoCommit.js.
 */
const useAnnounceSeoCommit = (title) => {
  useEffect(() => { notifySeoCommit(); }, [title]);
};

export const Seo = ({ title, description, path = '/', schema = null, ogImage, noindex = false, robots = 'noindex, nofollow' }) => {
  useDropStaticSeoTags();
  useAnnounceSeoCommit(title);
  const url = `${COMPANY.siteUrl}${path}`;
  const image = ogImage || `${COMPANY.siteUrl}/images/hero-home.jpg`;
  const schemas = Array.isArray(schema) ? schema : schema ? [schema] : [];
  if (noindex) {
    // `robots` lets a page pick the exact directive (/review uses plain "noindex" — Batch B, Part 3.3);
    // the 404 page keeps the stricter default.
    return (
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="robots" content={robots} />
      </Helmet>
    );
  }
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={COMPANY.name} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta name="twitter:card" content="summary_large_image" />
      {schemas.map((s, i) => (
        <script key={`${s['@type'] || 'schema'}-${i}`} type="application/ld+json">{JSON.stringify(s)}</script>
      ))}
    </Helmet>
  );
};

// JSON-LD builders live in ../seo/schema.js (pure data, shared with the HTML shell).
export { serviceSchema, areaServiceSchema, faqSchema, articleSchema, itemListSchema, organizationSchema, breadcrumbSchema, BUSINESS_SCHEMA } from '../seo/schema';
